<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import type { MenuItem } from "primevue/menuitem";

import { isSidebarLinkActive } from "@/composables/sidebar-nav-active";

const props = withDefaults(
  defineProps<{
    item: MenuItem;
    /** Tools (PanelMenu) children: skip `btn-ghost` so active `text-primary` isn’t washed out gray */
    nested?: boolean;
  }>(),
  { nested: false },
);

const route = useRoute();

const toPath = computed(() => props.item.route as string);

const isActive = computed(
  () => !!(props.item.route && isSidebarLinkActive(route.path, props.item.route)),
);

const linkClass = computed(() => {
  const active = isActive.value
    ? "border-l-primary bg-primary/20 font-semibold !text-primary [&_i]:!text-primary"
    : "";

  if (props.nested) {
    return [
      "link-ns box-border flex w-full min-h-10 rounded-none border-b-[0.5px] border-l-[3px] border-solid border-b-base-content/[0.07] border-l-transparent px-2 py-2 text-left text-base font-medium text-base-content transition-colors duration-150",
      "cursor-pointer select-none touch-manipulation hover:bg-base-200/55 dark:hover:bg-base-200/25",
      active,
    ];
  }

  return [
    "link-ns btn btn-ghost btn-sm box-border w-full min-h-11 rounded-none border-b-[0.5px] border-l-[3px] border-solid border-b-base-content/[0.07] border-l-transparent px-2 py-2.5 text-left text-base font-medium transition-colors duration-150",
    "cursor-pointer select-none touch-manipulation",
    active,
  ];
});
</script>

<template>
  <router-link
    :to="toPath"
    :class="linkClass"
    :aria-current="isActive ? 'page' : undefined"
  >
    <div class="flex items-center w-full min-w-0 gap-2.5">
      <template v-if="props.item.image">
        <img :src="props.item.image" class="size-5 shrink-0 rounded" alt="" />
      </template>
      <template v-else-if="props.item.icon">
        <i
          :class="[
            'p-menu-item-icon pi shrink-0',
            props.item.icon,
            isActive ? '!text-primary' : nested ? 'text-primary/85' : 'text-primary/80',
          ]"
        />
      </template>
      <div
        class="min-w-0 truncate leading-snug"
        :class="isActive ? '!text-primary' : ''"
      >
        {{ props.item.label }}
      </div>
    </div>
  </router-link>
</template>